import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap font-semibold transition-[background-color,color,box-shadow,transform,opacity] duration-150 ease-out-soft active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-[1.1em] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-volt-500 text-white shadow-glow hover:bg-volt-400',
        secondary: 'bg-ink-700 text-fg ring-1 ring-line-strong hover:bg-ink-600',
        ghost: 'text-fg-muted hover:bg-white/[0.06] hover:text-fg',
        outline: 'text-fg ring-1 ring-line-strong hover:bg-white/[0.04]',
        danger: 'bg-danger/12 text-danger ring-1 ring-danger/30 hover:bg-danger/20',
        glass: 'glass text-fg ring-1 ring-line-strong hover:bg-ink-700/80',
      },
      size: {
        sm: 'h-9 rounded-md px-3 text-sm',
        md: 'h-11 rounded-lg px-4 text-[0.95rem]',
        lg: 'h-13 rounded-xl px-6 text-base',
        icon: 'size-10 rounded-full',
        'icon-sm': 'size-8 rounded-full text-sm',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
